import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { forwardingAPI } from '../services/api';
import { FiPhoneForwarded, FiPlus, FiEdit2, FiTrash2, FiCopy, FiX, FiCheck, FiDownload } from 'react-icons/fi';
import { formatDate, formatDateTime } from '../utils/formatters';

// Sana va vaqt formada ALOHIDA maydonlarda yuritiladi (kiritish oson bo'lsin),
// serverga esa avvalgidek bitta `start_at`/`end_at` qiymati yuboriladi — baza,
// backend va qoidalar ro'yxati o'zgarmaydi.
const emptyForm = {
  name: '', description: '', destination: '', enabled: true,
  start_date: '', start_time: '08:00', end_date: '', end_time: '20:00',
  recurrence: 'once', weekdays: [], daily_start: '09:00', daily_end: '18:00',
};

// Vaqt bo'sh qoldirilsa (haftalik qoidaning ixtiyoriy muddati): kun boshi / kun oxiri.
export const FALLBACK_START = '00:00';
export const FALLBACK_END = '23:59';

/** Alohida sana va vaqtni server kutgan ko'rinishga qo'shadi. Sana bo'sh bo'lsa — qiymat yo'q. */
export const joinDateTime = (date, time, fallback) => (date ? `${date}T${time || fallback}` : '');

// Dushanbadan boshlanadi (panelning qolgan qismi kabi), qiymatlar JS getDay() bilan bir xil.
const WEEKDAYS = [
  { id: 1, short: 'Du' }, { id: 2, short: 'Se' }, { id: 3, short: 'Ch' },
  { id: 4, short: 'Pa' }, { id: 5, short: 'Ju' }, { id: 6, short: 'Sha' },
  { id: 0, short: 'Ya' },
];

const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

/** Qoidaning jadvalini bir qatorda ko'rsatish. */
function scheduleLabel(rule) {
  if (rule.recurrence === 'weekly') {
    const days = WEEKDAYS.filter(d => (rule.weekdays || []).includes(d.id)).map(d => d.short).join(', ');
    return `Har hafta: ${days} ${hhmm(rule.daily_start)}–${hhmm(rule.daily_end)}`;
  }
  return null;
}

export default function ForwardingPage() {
  const [rules, setRules] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([forwardingAPI.list(), forwardingAPI.status()]);
      setRules(r.data.rules || []);
      setStatus(s.data);
    } catch (err) {
      console.error('Forwarding load error:', err);
      toast.error('Qoidalarni yuklashda xato');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setShowForm(true); };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await forwardingAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yonaltirish_qoidalari.xls';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Eksport qilishda xato');
    } finally {
      setExporting(false);
    }
  };

  // Tahrirlash ham, nusxa olish ham AYNAN shu ko'chirishdan foydalanadi —
  // yangi maydon qo'shilganda ikkalasi birdan yangilanadi.
  const formFromRule = (rule) => {
    const s = splitLocal(rule.start_at);
    const e = splitLocal(rule.end_at);
    return {
      name: rule.name || '',
      description: rule.description || '',
      destination: rule.destination || '',
      start_date: s.date,
      start_time: s.time || '08:00',
      end_date: e.date,
      end_time: e.time || '20:00',
      enabled: rule.enabled,
      recurrence: rule.recurrence || 'once',
      weekdays: rule.weekdays || [],
      daily_start: hhmm(rule.daily_start) || '09:00',
      daily_end: hhmm(rule.daily_end) || '18:00',
    };
  };

  const openEdit = (rule) => {
    setEditId(rule.id);
    setForm(formFromRule(rule));
    setShowForm(true);
  };

  // Nusxa DARHOL saqlanmaydi, forma to'ldirilgan holda ochiladi. Sababi: bir xil
  // jadvalli ikkita faol qoida server tomonidan rad etiladi (to'qnashuv, 409),
  // shuning uchun foydalanuvchi avval kunini/vaqtini yoki raqamini o'zgartirishi kerak.
  const openCopy = (rule) => {
    setEditId(null);
    setForm({ ...formFromRule(rule), name: `${rule.name || ''} (nusxa)`.trim() });
    setShowForm(true);
    toast('Nusxa tayyor — jadval yoki raqamni o\'zgartirib saqlang', { icon: '📋' });
  };

  // Bir kunlik qoida — eng ko'p uchraydigan holat (masalan shanba 08:00–20:00).
  // Shuning uchun boshlanish sanasi tanlanganda, tugash sanasi hali bo'sh yoki
  // boshlanish sanasiga teng bo'lsa, u ham avtomatik o'sha kunga qo'yiladi.
  const pickStartDate = (value) => {
    setForm(prev => ({
      ...prev,
      start_date: value,
      end_date: (!prev.end_date || prev.end_date === prev.start_date) ? value : prev.end_date,
    }));
  };

  const toggleWeekday = (id) => {
    setForm(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(id)
        ? prev.weekdays.filter(d => d !== id)
        : [...prev.weekdays, id].sort((a, b) => a - b),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.destination) {
      toast.error('Nom va raqamni to\'ldiring');
      return;
    }
    if (form.recurrence === 'weekly') {
      if (form.weekdays.length === 0) {
        toast.error('Kamida bitta hafta kunini tanlang');
        return;
      }
      if (!form.daily_start || !form.daily_end || form.daily_start === form.daily_end) {
        toast.error('Kunlik vaqtni to\'g\'ri kiriting');
        return;
      }
    } else if (!form.start_date || !form.start_time || !form.end_date || !form.end_time) {
      toast.error('Boshlanish va tugash sanasi hamda vaqtini to\'ldiring');
      return;
    }
    const start_at = joinDateTime(form.start_date, form.start_time, FALLBACK_START);
    const end_at = joinDateTime(form.end_date, form.end_time, FALLBACK_END);
    if (start_at && end_at && new Date(end_at) <= new Date(start_at)) {
      toast.error('Tugash vaqti boshlanishdan keyin bo\'lishi kerak');
      return;
    }
    // Serverga AVVALGI shakl yuboriladi — alohida maydonlar faqat forma ichida yashaydi.
    const payload = {
      name: form.name,
      description: form.description,
      destination: form.destination,
      enabled: form.enabled,
      recurrence: form.recurrence,
      weekdays: form.weekdays,
      daily_start: form.daily_start,
      daily_end: form.daily_end,
      start_at: start_at || null,
      end_at: end_at || null,
    };
    setSaving(true);
    try {
      if (editId) {
        await forwardingAPI.update(editId, payload);
        toast.success('Qoida yangilandi');
      } else {
        await forwardingAPI.create(payload);
        toast.success('Qoida yaratildi');
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Saqlashda xato');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`"${rule.name}" qoidasini o'chirishni tasdiqlaysizmi?`)) return;
    try {
      await forwardingAPI.delete(rule.id);
      toast.success('Qoida o\'chirildi');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'O\'chirishda xato');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Qo'ng'iroqlarni yo'naltirish</h1>
          <p className="page-subtitle">Rejalashtirilgan qo'ng'iroqlarni boshqa raqamga yo'naltirish</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-ghost" onClick={exportExcel} disabled={exporting || rules.length === 0}>
            <FiDownload size={16} /> {exporting ? 'Yuklanmoqda...' : "Excel"}
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            <FiPlus size={16} /> Yangi qoida
          </button>
        </div>
      </div>

      {/* Live status banner */}
      <div className="card" style={{ marginBottom: '20px', borderLeft: `4px solid ${status?.active ? '#10b981' : '#64748b'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '4px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px',
            background: status?.active ? '#10b98120' : '#64748b20',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiPhoneForwarded size={22} color={status?.active ? '#10b981' : '#64748b'} />
          </div>
          <div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Yo'naltirish holati</div>
            {status?.active ? (
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                FAOL — «{status.rule.name}» → {status.rule.destination}
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)', marginLeft: 8 }}>
                  {scheduleLabel(status.rule)
                    || `${formatDateTime(status.rule.start_at)} — ${formatDateTime(status.rule.end_at)}`}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-secondary)' }}>NOFAOL — normal call-center oqimi</div>
            )}
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <span className="card-title">{editId ? 'Qoidani tahrirlash' : 'Yangi yo\'naltirish qoidasi'}</span>
            <button className="btn-icon btn-ghost" onClick={() => setShowForm(false)}><FiX size={18} /></button>
          </div>
          <form onSubmit={submit} className="adm-g2" style={{ gap: '16px', padding: '4px' }}>
            <Field label="Qoida nomi *">
              <input className="input" value={form.name} maxLength={120}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Masalan: Tushlik vaqti" />
            </Field>
            <Field label="Yo'naltiriladigan raqam *">
              <input className="input" value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="998901234567" />
            </Field>
            <Field label="Qoida turi *">
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'once', t: 'Bir martalik' },
                  { v: 'weekly', t: 'Har hafta (navbatchilik)' },
                ].map(o => (
                  <button key={o.v} type="button"
                    onClick={() => setForm({ ...form, recurrence: o.v })}
                    className={`btn ${form.recurrence === o.v ? 'btn-primary' : 'btn-ghost'}`}
                    style={{ flex: 1, justifyContent: 'center' }}>
                    {o.t}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Tavsif">
              <input className="input" value={form.description} maxLength={500}
                onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ixtiyoriy izoh" />
            </Field>

            {form.recurrence === 'weekly' ? (
              <>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Hafta kunlari *">
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {WEEKDAYS.map(d => {
                        const on = form.weekdays.includes(d.id);
                        return (
                          <button key={d.id} type="button" onClick={() => toggleWeekday(d.id)}
                            style={{
                              minWidth: 52, padding: '9px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                              cursor: 'pointer',
                              background: on ? '#3b82f6' : 'transparent',
                              color: on ? '#fff' : 'var(--text-secondary)',
                              border: `1px solid ${on ? '#3b82f6' : 'var(--border, #2d3a4f)'}`,
                            }}>
                            {d.short}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </div>
                <Field label="Kunlik boshlanish *">
                  <input type="time" className="input" value={form.daily_start}
                    onChange={(e) => setForm({ ...form, daily_start: e.target.value })} />
                </Field>
                <Field label="Kunlik tugash *">
                  <input type="time" className="input" value={form.daily_end}
                    onChange={(e) => setForm({ ...form, daily_end: e.target.value })} />
                </Field>
                <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Tugash vaqti boshlanishdan kichik bo'lsa oraliq yarim tundan o'tadi
                  (masalan 18:00 → 09:00 — tungi navbatchilik).
                  {' '}Amal qilish muddati (pastdagi sanalar) ixtiyoriy — bo'sh qoldirilsa jadval muddatsiz ishlaydi.
                </div>
                <Field label="Amal qilish boshlanishi — sana">
                  <input type="date" className="input" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </Field>
                <Field label="Boshlanish vaqti">
                  <input type="time" className="input" value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </Field>
                <Field label="Amal qilish tugashi — sana">
                  <input type="date" className="input" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </Field>
                <Field label="Tugash vaqti">
                  <input type="time" className="input" value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                </Field>
              </>
            ) : (
              <>
                <Field label="Boshlanish sanasi *">
                  <input type="date" className="input" value={form.start_date}
                    onChange={(e) => pickStartDate(e.target.value)} />
                </Field>
                <Field label="Boshlanish vaqti *">
                  <input type="time" className="input" value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </Field>
                <Field label="Tugash sanasi *">
                  <input type="date" className="input" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </Field>
                <Field label="Tugash vaqti *">
                  <input type="time" className="input" value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                </Field>
                <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Bir kunlik qoidada tugash sanasi boshlanish sanasi bilan avtomatik to'ldiriladi —
                  faqat vaqtlarni qo'yish yetarli. Tungi navbatchilik uchun tugash sanasiga keyingi kunni tanlang.
                </div>
              </>
            )}
            <Field label="Holati">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                <span style={{ color: 'var(--text-secondary)' }}>Yoqilgan</span>
              </label>
            </Field>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Bekor qilish</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                <FiCheck size={16} /> {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rules table */}
      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Holat</th>
                <th>Nomi</th>
                <th>Raqam</th>
                <th>Turi</th>
                <th>Jadval</th>
                <th>Yoqilgan</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Hozircha qoidalar yo'q</td></tr>
              ) : rules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <span className={`badge ${rule.is_active ? 'badge-success' : 'badge-neutral'}`}>
                      <span className="badge-dot" />{rule.is_active ? 'Faol' : 'Kutilmoqda'}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    {rule.name}
                    {rule.description && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rule.description}</div>}
                  </td>
                  <td style={{ fontWeight: 600 }}>{rule.destination}</td>
                  <td>
                    <span className={`badge ${rule.recurrence === 'weekly' ? 'badge-info' : 'badge-neutral'}`}>
                      {rule.recurrence === 'weekly' ? 'Har hafta' : 'Bir martalik'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {rule.recurrence === 'weekly' ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{scheduleLabel(rule)}</div>
                        {(rule.start_at || rule.end_at) && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            muddat: {rule.start_at ? formatDateTime(rule.start_at) : '—'}
                            {' → '}{rule.end_at ? formatDateTime(rule.end_at) : '—'}
                          </div>
                        )}
                      </>
                    ) : (
                      /* Faqat BOSHLANISH SANASI: tugash qatori ustunni band qilardi,
                         soat esa qoidalarda deyarli doim bir xil (08:00). */
                      <>{formatDate(rule.start_at)}</>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${rule.enabled ? 'badge-success' : 'badge-danger'}`}>
                      {rule.enabled ? 'Ha' : "Yo'q"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <button className="btn-icon btn-ghost" onClick={() => openEdit(rule)} title="Tahrirlash"><FiEdit2 size={16} /></button>
                      <button className="btn-icon btn-ghost" onClick={() => openCopy(rule)} title="Nusxa olish"><FiCopy size={16} /></button>
                      <button className="btn-icon btn-ghost" style={{ color: '#ef4444' }} onClick={() => remove(rule)} title="O'chirish"><FiTrash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{label}</label>
      {children}
    </div>
  );
}

// Bazadagi vaqt belgisini (timestamptz) formaning alohida maydonlariga ajratadi:
// mahalliy sana (YYYY-MM-DD) va mahalliy vaqt (HH:mm). Mavjud qoidalarni
// tahrirlash va nusxa olish shu yo'l bilan to'g'ri ishlaydi.
export function splitLocal(ts) {
  if (!ts) return { date: '', time: '' };
  const d = new Date(ts);
  if (isNaN(d)) return { date: '', time: '' };
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
